import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, Field, inputCls, money, Pill, useDirectory } from "./common";

const empty = { customer_id: "", service_performed: "", amount_due: "", due_date: "", status: "unpaid", notes: "" };

export default function InvoicesManager() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null);
  const { customers, custName } = useDirectory();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/invoices"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setModal((m) => ({ ...m, [k]: v }));
  const save = async () => {
    try { await api.post("/invoices", modal); toast.success("Invoice created"); setModal(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const markPaid = async (id, method) => {
    try { await api.put(`/invoices/${id}`, { status: "paid", payment_method: method }); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const unpaid = (items || []).filter((i) => i.status !== "paid");

  return (
    <div data-testid="invoices-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-600 text-charcoal">Invoices</h2>
        <button onClick={() => setModal({ ...empty })} data-testid="new-invoice-btn" className="inline-flex items-center gap-2 bg-emerald text-white px-4 py-2.5 rounded-sm text-sm font-600 hover:bg-emerald-dark"><Plus className="w-4 h-4" /> New Invoice</button>
      </div>
      {items && unpaid.length > 0 && (
        <div className="bg-gold/10 border border-gold/30 rounded-sm px-4 py-3 mb-5 text-sm text-charcoal" data-testid="invoices-outstanding">
          <strong>{unpaid.length}</strong> outstanding invoice{unpaid.length === 1 ? "" : "s"} · {money(unpaid.reduce((s, i) => s + (i.amount_due || 0), 0))} due
        </div>
      )}
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-olive" /> : items.length === 0 ? (
        <p className="text-olive" data-testid="invoices-empty">No invoices yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((inv) => (
            <div key={inv.id} className="bg-white border border-border rounded-sm p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`invoice-${inv.id}`}>
              <div>
                <div className="flex items-center gap-2"><span className="font-600 text-charcoal">{inv.invoice_number}</span><Pill status={inv.status} /></div>
                <p className="text-olive text-sm mt-0.5">{custName(inv.customer_id)} · {inv.service_performed}</p>
                <p className="text-charcoal font-600 mt-0.5">{money(inv.amount_due)}{inv.due_date ? ` · due ${inv.due_date}` : ""}</p>
              </div>
              {inv.status !== "paid" && (
                <select className="border border-border rounded-sm px-2 py-1.5 text-sm" data-testid={`invoice-pay-${inv.id}`} defaultValue="" onChange={(e) => e.target.value && markPaid(inv.id, e.target.value)}>
                  <option value="">Mark paid…</option>
                  {["Cash", "Check", "Venmo", "Zelle", "Card"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
              {inv.status === "paid" && <span className="text-sm text-emerald">Paid{inv.payment_method ? ` · ${inv.payment_method}` : ""}</span>}
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title="New Invoice" onClose={() => setModal(null)} testid="invoice-modal">
          <Field label="Customer"><select className={inputCls} data-testid="invoice-customer" value={modal.customer_id} onChange={(e) => set("customer_id", e.target.value)}><option value="">Select…</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}</select></Field>
          <Field label="Service Performed"><input className={inputCls} data-testid="invoice-service" value={modal.service_performed} onChange={(e) => set("service_performed", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount Due $"><input type="number" className={inputCls} data-testid="invoice-amount" value={modal.amount_due} onChange={(e) => set("amount_due", e.target.value)} /></Field>
            <Field label="Due Date"><input type="date" className={inputCls} value={modal.due_date} onChange={(e) => set("due_date", e.target.value)} /></Field>
          </div>
          <Field label="Notes"><textarea rows={2} className={inputCls} value={modal.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          <button onClick={save} data-testid="invoice-save-btn" className="bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark">Save</button>
        </Modal>
      )}
    </div>
  );
}
